import { ApiErrorResponse } from '../types/auth';
import { Category, CreateCategoryPayload, UpdateCategoryPayload } from '../types/category';
import { CATEGORY_ENDPOINTS } from '../utils/endPoints';
import { createApiClient } from '../utils/axiosConfig';
import axios from 'axios';

export type { ApiErrorResponse } from '../types/auth';

const apiClient = createApiClient();

export const getCategoriesByStore = async (storeId: number): Promise<Category[]> => {
  try {
    const response = await apiClient.get<any>(CATEGORY_ENDPOINTS.GET_ALL);
    
    if (response.data.records && Array.isArray(response.data.records)) {
      return response.data.records;
    }
    
    if (Array.isArray(response.data)) {
      return response.data;
    }
    
    if (response.data.categories && Array.isArray(response.data.categories)) {
      return response.data.categories;
    }
    
    return [];
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const apiError = error.response.data as ApiErrorResponse;
      throw new Error(apiError.message || 'Failed to fetch categories');
    }
    console.error('Get categories error:', error);
    throw new Error('An unexpected error occurred while fetching categories.');
  }
};

export const getStoresByCategory = async (categoryId: number): Promise<Array<{ id: number; store_name: string }>> => {
  try {
    const response = await apiClient.get<{ stores: Array<{ id: number; store_name: string }> }>(
      CATEGORY_ENDPOINTS.STORES_BY_CATEGORY(categoryId)
    );
    return response.data.stores ?? [];
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const apiError = error.response.data as ApiErrorResponse;
      throw new Error(apiError.message || 'Failed to fetch stores for category');
    }
    console.error('Get stores by category error:', error);
    throw new Error('An unexpected error occurred while fetching stores for category.');
  }
};

export const createCategory = async (storeId: number, categoryData: CreateCategoryPayload): Promise<{ category: Category, message: string }> => {
  try {
    const payload = {
      ...categoryData,
      store_id: storeId,
      category_name: categoryData.name
    };
    
    const response = await apiClient.post<{ category: Category, message: string }>(CATEGORY_ENDPOINTS.CREATE, payload);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const apiError = error.response.data as ApiErrorResponse;
      throw new Error(apiError.message || 'Failed to create category');
    }
    console.error('Create category error:', error);
    throw new Error('An unexpected error occurred while creating the category.');
  }
};

export const updateCategory = async (categoryId: number, categoryData: UpdateCategoryPayload): Promise<{ category: Category, message: string }> => {
  try {
    const payload = {
      ...categoryData,
      category_id: categoryId,
      category_name: categoryData.name
    };
    
    const response = await apiClient.put<{ category: Category, message: string }>(CATEGORY_ENDPOINTS.UPDATE, payload);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const apiError = error.response.data as ApiErrorResponse;
      throw new Error(apiError.message || 'Failed to update category');
    }
    console.error('Update category error:', error);
    throw new Error('An unexpected error occurred while updating the category.');
  }
};

export const deleteCategory = async (categoryId: number): Promise<{ message: string }> => {
  try {
    const response = await apiClient.delete<{ message: string }>(CATEGORY_ENDPOINTS.DELETE(categoryId));
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const apiError = error.response.data as ApiErrorResponse;
      throw new Error(apiError.message || 'Failed to delete category');
    }
    console.error('Delete category error:', error);
    throw new Error('An unexpected error occurred while deleting the category.');
  }
}; 